/**
 * Sprint 74 — Frontend error reporter sink.
 *
 * Hooks `window.onerror` + `unhandledrejection` + a React ErrorBoundary
 * to ship JS crashes to /api/client-errors. Lightly de-duplicated so a
 * single broken page doesn't flood the server with thousands of copies
 * of the same error.
 *
 * Wire-up:
 *   import { installErrorReporter } from "./utils/errorReporter";
 *   installErrorReporter(); // call once at app bootstrap (App.js / index.js)
 *
 * For React component errors, wrap with `<RootErrorBoundary>` (this file).
 */
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const ENDPOINT = `${BACKEND}/api/client-errors`;

const recentSignatures = new Map(); // signature -> last sent timestamp
const DEDUPE_WINDOW_MS = 60_000;
const MAX_BUFFER = 30;

function shouldSend(sig) {
  const now = Date.now();
  // Trim old entries
  if (recentSignatures.size > MAX_BUFFER) {
    for (const [k, ts] of recentSignatures) {
      if (now - ts > DEDUPE_WINDOW_MS) recentSignatures.delete(k);
    }
  }
  const last = recentSignatures.get(sig);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentSignatures.set(sig, now);
  return true;
}

export async function reportClientError({ message, stack, source = "window_error", severity = "error" }) {
  try {
    if (!message) return;
    const sig = `${source}|${(message || "").slice(0, 200)}`;
    if (!shouldSend(sig)) return;
    const payload = {
      message: String(message).slice(0, 2000),
      stack: stack ? String(stack).slice(0, 8000) : null,
      route: typeof window !== "undefined" ? window.location.pathname : null,
      source,
      severity,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 400) : null,
    };
    // Use a fresh axios instance so failures don't trigger global interceptors.
    await axios.post(ENDPOINT, payload, { timeout: 4000 });
  } catch {
    /* swallow — never let the reporter itself blow up */
  }
}

export function installErrorReporter() {
  if (typeof window === "undefined") return;
  if (window.__pickngoReporterInstalled) return;
  window.__pickngoReporterInstalled = true;

  window.addEventListener("error", (event) => {
    reportClientError({
      message: event?.message || "Uncaught error",
      stack: event?.error?.stack || null,
      source: "window_error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    reportClientError({
      message: (reason && (reason.message || String(reason))) || "Unhandled promise rejection",
      stack: reason?.stack || null,
      source: "unhandled_rejection",
    });
  });
}
